import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { getErrorMessage } from "@/lib/api";
import { Clapperboard, Film, Loader2, Wand2, Zap } from "lucide-react";

const IDEAS = [
  "Un renard roux qui court dans une forêt brumeuse au lever du soleil, style cinématographique",
  "Une mégapole futuriste sous la pluie néon, plans aériens de nuit",
  "Un café parisien en terrasse, lumière dorée de fin d'après-midi, ralenti",
  "Un astronaute flottant devant la Terre, reflets bleus sur sa visière",
];

const Studio = () => {
  const { user, refreshUser } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("kling");
  const [duration, setDuration] = useState("5");
  const [ratio, setRatio] = useState("16:9");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/models").then((r) => setModels(r.data.models)).catch(() => {});
  }, []);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/videos/${id}`);
        setJob(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(pollRef.current);
          setBusy(false);
          refreshUser();
          if (data.status === "completed") toast.success("Votre vidéo est prête !");
          else toast.error(data.error || "La génération a échoué. Vos crédits ont été recrédités.");
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
  };

  const generate = async () => {
    if (prompt.trim().length < 3) return toast.error("Décrivez d'abord votre vidéo");
    setBusy(true);
    setJob(null);
    try {
      const { data } = await api.post("/videos/generate", { prompt, model, duration, aspect_ratio: ratio });
      setJob(data);
      refreshUser();
      poll(data.id);
    } catch (e) {
      setBusy(false);
      toast.error(getErrorMessage(e));
    }
  };

  const currentModel = models.find((m) => m.id === model);

  return (
    <div className="noise relative min-h-[80vh]">
      <div className="absolute -top-24 right-1/4 w-96 h-96 rounded-full bg-[#EC4899]/15 blur-[130px] pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl sm:text-4xl font-black" data-testid="studio-title">
            Studio de <span className="text-gradient">création</span>
          </h1>
          <span className="flex items-center gap-1.5 rounded-full border border-[#EC4899]/40 bg-[#EC4899]/10 px-4 py-1.5 text-sm font-semibold text-[#EC4899]" data-testid="studio-credits-badge">
            <Zap className="w-4 h-4" /> {user?.credits ?? 0} crédits
          </span>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 rounded-3xl border border-border bg-card p-6" data-testid="studio-form-panel">
            <label className="text-sm font-medium" htmlFor="studio-prompt">Votre scène</label>
            <Textarea
              id="studio-prompt"
              rows={5}
              placeholder="Ex. : Un surfeur au coucher du soleil sur une vague dorée, drone qui suit, cinématique…"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="mt-2 bg-background"
              data-testid="studio-prompt-textarea"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {IDEAS.map((idea, i) => (
                <button
                  key={i}
                  onClick={() => setPrompt(idea)}
                  className="text-xs rounded-full border border-border px-3 py-1 text-muted-foreground hover:text-foreground hover:border-[#8B5CF6]/60 transition-colors"
                  data-testid={`studio-idea-chip-${i}`}
                >
                  {idea.slice(0, 38)}…
                </button>
              ))}
            </div>

            <div className="mt-6">
              <span className="text-sm font-medium">Modèle</span>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {models.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => { setModel(m.id); setDuration(m.durations[0]); }}
                    className={`rounded-xl border p-3 text-left transition-colors ${model === m.id ? "border-[#8B5CF6] bg-[#8B5CF6]/10" : "border-border hover:border-muted"}`}
                    data-testid={`studio-model-option-${m.id}`}
                  >
                    <span className="block text-sm font-semibold">{m.label}</span>
                    <span className="block text-xs text-muted-foreground">{m.credits} crédits</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium">Format</span>
                <div className="mt-2 flex gap-2">
                  {["16:9", "9:16"].map((r) => (
                    <button
                      key={r}
                      onClick={() => setRatio(r)}
                      className={`flex-1 rounded-lg border py-2 text-sm transition-colors ${ratio === r ? "border-[#EC4899] text-[#EC4899]" : "border-border text-muted-foreground"}`}
                      data-testid={`studio-ratio-option-${r.replace(":", "-")}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              {currentModel && currentModel.durations.length > 1 && (
                <div>
                  <span className="text-sm font-medium">Durée</span>
                  <div className="mt-2 flex gap-2">
                    {currentModel.durations.map((d) => (
                      <button
                        key={d}
                        onClick={() => setDuration(d)}
                        className={`flex-1 rounded-lg border py-2 text-sm transition-colors ${duration === d ? "border-[#EC4899] text-[#EC4899]" : "border-border text-muted-foreground"}`}
                        data-testid={`studio-duration-option-${d}s`}
                      >
                        {d}s
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={generate}
              disabled={busy || !user}
              className="mt-8 w-full h-12 text-base bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white border-0"
              data-testid="studio-generate-button"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération en cours…</> : <><Wand2 className="w-4 h-4 mr-2" /> Générer ma vidéo</>}
            </Button>
            {!user && (
              <p className="mt-3 text-center text-sm text-muted-foreground">
                <Link to="/connexion" className="text-[#8B5CF6] hover:underline">Connectez-vous</Link> pour générer.
              </p>
            )}
          </div>

          <div className="lg:col-span-3">
            <div className={`rounded-3xl border border-border overflow-hidden bg-card flex items-center justify-center ${job?.status === "completed" ? "" : "min-h-[320px] lg:min-h-full"}`} data-testid="studio-preview-panel">
              {job?.status === "completed" ? (
                <video src={`${process.env.REACT_APP_BACKEND_URL}/api/videos/${job.id}/file`} controls autoPlay className="w-full" data-testid="studio-video-player" />
              ) : busy || job?.status === "pending" || job?.status === "processing" ? (
                <div className="text-center py-20 px-6">
                  <Film className="w-12 h-12 mx-auto text-[#8B5CF6] animate-pulse" />
                  <p className="mt-4 font-display font-medium" data-testid="studio-generating-text">L'IA tourne votre clip…</p>
                  <p className="mt-2 text-sm text-muted-foreground">Cela prend généralement 1 à 3 minutes.</p>
                </div>
              ) : (
                <div className="text-center py-20 px-6">
                  <Clapperboard className="w-12 h-12 mx-auto text-muted-foreground" />
                  <p className="mt-4 text-muted-foreground" data-testid="studio-empty-text">Votre vidéo apparaîtra ici.</p>
                </div>
              )}
            </div>
            {job?.status === "failed" && (
              <p className="mt-3 text-sm text-destructive" data-testid="studio-error-text">Échec : {job.error}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Studio;
